import {useCallback, useEffect, useMemo, useState} from 'react';
import {getMyApis} from '../api/meApi';

// 모듈 레벨 캐시로 최초 조회 이후 재사용
let cachedApis = null; // Array<{ method: string, apiUrl: string }>
let cacheError = null;
let cacheLoading = false;
let cacheLoadedAt = 0;

const stripQuery = (url='') => url.split('?')[0];
const toPath = (url='') => {
  try {
    // 이미 절대 경로 형태라 가정하고 쿼리 제거
    return stripQuery(url) || '';
  } catch {
    return stripQuery(url) || '';
  }
};

// 템플릿 패턴 매칭: /a/{id}/b 와 /a/123/b 일치
const matchPath = (pattern='', target='') => {
  if (!pattern || !target) return false;
  const p = toPath(pattern).replace(/\/$/, '');
  const t = toPath(target).replace(/\/$/, '');
  const pSegs = p.split('/').filter(Boolean);
  const tSegs = t.split('/').filter(Boolean);
  if (pSegs.length !== tSegs.length) return false;
  for (let i=0;i<pSegs.length;i++) {
    const ps = pSegs[i];
    const ts = tSegs[i];
    const isVar = ps.startsWith('{') && ps.endsWith('}');
    if (!isVar && ps !== ts) return false;
  }
  return true;
};

const normalizeMethod = (m='') => String(m || '').toUpperCase();

/**
 * useApiPermission
 * 현재 서버 측 API 개별 권한(read/execute) 제거됨에 따라
 * 모든 API 호출을 허용하는 최소 훅. 추후 세분화 필요시 재도입.
 * 반환: { can(method,url): boolean, loading: false, get(): { allowed: true } }
 */
export default function useApiPermission() {
  const [list, setList] = useState(cachedApis);
  const [loading, setLoading] = useState(cacheLoading || !cachedApis);
  const [error, setError] = useState(cacheError);

  // 권한 목록 로딩 로직 추출 (초기/갱신 공용)
  const loadApis = useCallback(async () => {
    try {
      if (cachedApis) { setList(cachedApis); setLoading(false); return; }
      if (cacheLoading) { setLoading(true); return; }
      cacheLoading = true;
      setLoading(true);
      const res = await getMyApis(); // { code, data }
      const apis = Array.isArray(res?.data) ? res.data : [];
      cachedApis = apis.map(a => ({ method: normalizeMethod(a?.method), apiUrl: toPath(a?.apiUrl) }));
      cacheError = null;
      cacheLoadedAt = Date.now();
      setList(cachedApis);
    } catch (e) {
      cacheError = e;
      setError(e);
    } finally {
      cacheLoading = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => { if (mounted) await loadApis(); })();
    return () => { mounted = false; };
  }, [loadApis]);

  // 사용자 컨텍스트 변경 시 권한 캐시 무효화 및 재로딩
  useEffect(() => {
    const onAuthChanged = () => {
      cachedApis = null;
      cacheError = null;
      cacheLoadedAt = 0;
      setList(null);
      setError(null);
      setLoading(true);
      // 재로딩 트리거
      loadApis();
    };
    window.addEventListener('auth-context-changed', onAuthChanged);
    return () => window.removeEventListener('auth-context-changed', onAuthChanged);
  }, [loadApis]);

  const can = useCallback((method, url) => {
    if (!Array.isArray(cachedApis)) return false; // 로딩 전엔 보수적으로 비허용
    const m = normalizeMethod(method);
    const u = toPath(url);
    for (const a of cachedApis) {
      if (a.method !== m) continue;
      if (matchPath(a.apiUrl, u)) return true;
    }
    return false;
  }, []);

  // API 함수 혹은 {method,url} 메타를 받아 권한을 확인
  const canCall = useCallback((fnOrMeta) => {
    if (!fnOrMeta) return false;
    const meta = typeof fnOrMeta === 'function' ? fnOrMeta._perm : fnOrMeta;
    if (!meta || !meta.method || !meta.url) return false;
    return can(meta.method, meta.url);
  }, [can]);

  // 호환용
  const get = useCallback(() => ({ allowed: true }), []);

  // 빠른 조회를 위해 메소드별 URL Set 제공 (옵셔널)
  const index = useMemo(() => {
    const idx = new Map();
    (list || []).forEach(a => {
      const key = a.method;
      if (!idx.has(key)) idx.set(key, []);
      idx.get(key).push(a.apiUrl);
    });
    return idx;
  }, [list]);

  return { can, canCall, get, loading, error, index, loadedAt: cacheLoadedAt };
}
